import React from 'react';

/**
 * Generic error boundary. Catches render/lifecycle errors in its subtree so a
 * failure in one widget (e.g. the Google Maps loader throwing a cross-origin
 * error when the Maps API can't load) degrades to a friendly glass fallback
 * instead of unmounting the whole app.
 *
 * Pass `fallback` for custom UI, or `title`/`message` for the default card.
 */
class ErrorBoundary extends React.Component {
    state = { hasError: false, retries: 0 };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary]', this.props.label || '', error, info);
        // Some vendor scripts fail transiently on the very first mount of a
        // page (load-order race) but succeed on a re-render. Auto-retry once
        // before bothering the user; a persistent error still shows the card.
        if (this.state.retries < 1) {
            this.retryTimer = setTimeout(
                () => this.setState((s) => ({ hasError: false, retries: s.retries + 1 })),
                150
            );
        }
    }

    componentWillUnmount() {
        clearTimeout(this.retryTimer);
    }

    reset = () => this.setState({ hasError: false });

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.state.retries < 1) return null; // auto-retry pending — brief blank beats an error flash
        if (this.props.fallback) return this.props.fallback;

        return (
            <div
                style={{
                    margin: '96px auto 28px',
                    maxWidth: 460,
                    padding: 28,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12,
                    background: 'rgba(255, 255, 255, 0.82)',
                    border: '1px solid rgba(255, 255, 255, 0.7)',
                    borderRadius: 16,
                    boxShadow: '0 12px 36px rgba(15, 31, 46, 0.10)',
                    WebkitBackdropFilter: 'blur(20px)',
                    backdropFilter: 'blur(20px)',
                }}
            >
                <i
                    className="fas fa-triangle-exclamation"
                    style={{ fontSize: 26, color: 'var(--warning, #E5A000)' }}
                ></i>
                <div style={{ fontWeight: 800, color: 'var(--text)' }}>
                    {this.props.title || 'Something went wrong'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 360 }}>
                    {this.props.message ||
                        'This section failed to load. The rest of the app is unaffected.'}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={this.reset}>
                    <i className="fas fa-rotate-right" style={{ marginRight: 6 }}></i> Retry
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
